import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCatalog } from '../Features/BhajanSlice';
import { fetchNodes } from '../services/api';
import type { AnyGraphNode } from '../types/graphNodes';
import { collectDetailNodeIds } from './detailNodeIds';

/**
 * Returns the hydrated node array to pass to deriveBhajanDetail: the bhajan and
 * its detail neighbourhood with full data. While hydrating, `loading` is true.
 */
export function useDetailNodes(bhajanId: number): { nodes: AnyGraphNode[]; loading: boolean } {
  const catalog = useSelector(selectCatalog);
  const slim = useMemo(
    () => (catalog?.nodes ?? []),
    [catalog]
  );
  const [hydrated, setHydrated] = useState<AnyGraphNode[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!catalog) return; // wait for the slim index
    const byId = new Map(slim.map((node) => [node.nodeId, node]));
    const bhajan = byId.get(bhajanId);
    if (!bhajan) { setHydrated([]); setLoading(false); return; }

    let cancelled = false;
    setLoading(true);
    fetchNodes(collectDetailNodeIds(bhajan, byId))
      .then((full) => { if (!cancelled) { setHydrated(full); setLoading(false); } })
      .catch(() => { if (!cancelled) { setHydrated([bhajan]); setLoading(false); } });
    return () => { cancelled = true; };
  }, [catalog, slim, bhajanId]);

  return { nodes: hydrated ?? [], loading };
}
